import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate } from "react-router-dom";
import learningPortalImage from "../assets/images/learningportal.svg";
import Error from "../components/ui/Error";
import { useRegisterMutation } from "../features/auth/authApi";
import { ROLE_TYPE, URLPath } from "../utils/constantVariables";
import * as Yup from 'yup';



const initialValues = { name: '', email: '', password: '' };

const validationSchema = Yup.object().shape({
    name: Yup.string().required('Required'),
    email: Yup.string().email('Invalid email address').required('Required'),
    password: Yup.string().required('Required'),
    confirmPassword: Yup.string()
        .required('Confirm Password is required')
        .oneOf([Yup.ref('password'), null], 'Passwords must match'),
});

export default function Register() {
    //Variables
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const isAdminPage = pathname === URLPath.ADMIN;
    const [register, { isLoading }] = useRegisterMutation();


    //States
    const [error, setError] = useState('');

    //Functions
    const onSubmit = async (values, { setSubmitting }) => {
        setError('');
        setSubmitting(true);
        values.role = "student";
        delete values.confirmPassword;
        try {
            const { data, error } = await register(values);
            if (data?.accessToken && data?.user?.role === ROLE_TYPE.ADMIN) {
                navigate(URLPath.DASHBOARD);
            } else if (data?.accessToken && data?.user?.role === ROLE_TYPE.STUDENT) {
                navigate(URLPath.COURSE_PLAYER);
            } else if (error?.data) {
                setError(error.data);
            }
        } catch (error) {
            if (error?.response?.data) {
                setError(error.response.data);
            } else {
                setError('Something went wrong!');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const { handleSubmit, touched, errors, getFieldProps } = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
    });


    return (
        <>
            <Helmet>
                <title>Student Register</title>
            </Helmet>
            <section className="py-6 bg-primary h-screen grid place-items-center">
                <div className="mx-auto max-w-md px-5 lg:px-0">
                    <div>
                        <img
                            className="h-12 mx-auto"
                            src={learningPortalImage}
                            alt="logo"
                        />
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-slate-100">
                            {`Sign in to ${isAdminPage ? 'Admin' : 'Student'} Account`}
                        </h2>
                    </div>

                    <form onSubmit={handleSubmit} className="mt-8 space-y-6">
                        <div className="rounded-md shadow-sm -space-y-px">
                            <div>
                                <label htmlFor="name" className="sr-only">
                                    Name
                                </label>
                                <input
                                    id="name"
                                    type="text"
                                    className="login-input rounded-t-md"
                                    placeholder="Name"
                                    {...getFieldProps('name')}
                                />
                                {touched.name && errors.name && (
                                    <div className="text-red-600">{errors.name}</div>
                                )}
                            </div>
                            <div>
                                <label htmlFor="email-address" className="sr-only">
                                    Email address
                                </label>
                                <input
                                    id="email-address"
                                    type="email"
                                    autoComplete="email"
                                    className="login-input rounded-t-md"
                                    placeholder="Email address"
                                    {...getFieldProps('email')}
                                />
                                {touched.email && errors.email && (
                                    <div className="text-red-600">{errors.email}</div>
                                )}
                            </div>
                            <div>
                                <label htmlFor="password" className="sr-only">
                                    Password
                                </label>
                                <input
                                    id="password"
                                    type="password"
                                    autoComplete="current-password"
                                    className="login-input rounded-b-md text-light"
                                    placeholder="Password"
                                    required
                                    {...getFieldProps('password')}
                                />
                                {touched.password && errors.password && (
                                    <div className="text-red-600">{errors.password}</div>
                                )}
                            </div>
                            <div>
                                <label htmlFor="confirmPassword" className="sr-only">
                                    Conform Password
                                </label>
                                <input
                                    id="confirmPassword"
                                    type="password"
                                    autoComplete="current-password"
                                    className="login-input rounded-b-md text-light"
                                    placeholder="Confirm Password"
                                    required
                                    {...getFieldProps('confirmPassword')}
                                />
                                {touched.confirmPassword && errors.confirmPassword && (
                                    <div className="text-red-600">{errors.confirmPassword}</div>
                                )}
                            </div>
                        </div>
                        <div>
                            <button
                                type="submit"
                                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                                disabled={isLoading}
                            >
                                Create Account
                            </button>
                        </div>
                    </form>
                    {error !== "" && <Error message={error} />}
                </div>
            </section>

        </>

    );
}
